import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import Clarity from '@microsoft/clarity';
import {environment} from '../../../environments/environment';
import {afterFirstNavigationIdle} from '../boot/first-navigation';

const FREE_SUBSCRIPTION = 'guideglare_zero';

interface ClarityUpdate {
    subscriptions: string[];
    userId: string;
    email: string;
    displayName: string | null;
}

@Injectable({providedIn: 'root'})
export class ClarityService {

    private scriptLoaded = false;
    private recording = false;

    /**
     * Skript Clarity (~100 KB, třetí strana) se nesmí vkládat během startu — `update()` volá
     * `LayoutContainerComponent` už při aktivaci první route. Do dojetí první navigace + idle
     * si služba pamatuje jen poslední stav a aplikuje ho až potom.
     */
    private ready = false;
    private pending: ClarityUpdate | null = null;

    constructor() {
        afterFirstNavigationIdle(inject(Router), () => {
            this.ready = true;
            const pending = this.pending;
            this.pending = null;
            if (pending) {
                this.apply(pending);
            }
        });
    }

    /**
     * Call on every activeSubscriptions$ emit.
     *
     * FREE  + not recording → load script (once), grant consent, identify user → start recording
     * FREE  + already recording → no-op
     * PAID  + recording → withdraw consent → stop recording
     * PAID  + not recording → no-op
     */
    update(subscriptions: string[], userId: string, email: string, displayName: string | null): void {
        const next: ClarityUpdate = {subscriptions, userId, email, displayName};
        if (!this.ready) {
            this.pending = next;
            return;
        }
        this.apply(next);
    }

    private apply({subscriptions, userId, email, displayName}: ClarityUpdate): void {
        const projectId = environment.clarity?.projectId;
        if (!projectId || !userId) {
            return;
        }
        const isFree = subscriptions.includes(FREE_SUBSCRIPTION);

        if (isFree && !this.recording) {
            if (!this.scriptLoaded) {
                this.scriptLoaded = true;
                Clarity.init(projectId);
            }
            Clarity.identify(userId, undefined, undefined, email);
            Clarity.setTag('email', email);
            if (displayName) {
                Clarity.setTag('name', displayName);
            }
            Clarity.consent();
            this.recording = true;
        } else if (!isFree && this.recording) {
            Clarity.consent(false);
            this.recording = false;
        }
    }

    /**
     * Vlastní událost do Clarity. Když se nenahrává, tiše se zahodí — Clarity běží jen u FREE
     * účtů, takže volající se nemusí ptát, jestli má smysl událost posílat.
     */
    event(name: string): void {
        if (!this.recording) {
            return;
        }
        Clarity.event(name);
    }
}
